import { useEffect, useMemo, useState } from "react";
import {
  actionsFetchNudgeNeeded,
  filledWorkMonitorSlots,
  formatMonitorCacheAge,
} from "../../lib/softoneSlotSync";

export function WorkMonitorCacheBar({
  lang,
  monitor,
  actionsCache,
  loading,
  refreshing,
  fetchingActions,
  buttonsDisabled = false,
  error,
  notice,
  onRefresh,
  onFetchActions,
  actions,
}) {
  const t = (en, gr) => (lang === "gr" ? gr : en);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(id);
  }, []);

  const age = formatMonitorCacheAge(monitor?.fetched_at, lang, now);
  const actionsAge = formatMonitorCacheAge(actionsCache?.fetched_at, lang, now);
  const available = Boolean(monitor?.available);
  const actionsAvailable = Boolean(actionsCache?.available || actionsCache?.fetched_at);
  const filled = useMemo(() => filledWorkMonitorSlots(monitor?.slots), [monitor?.slots]);
  const nudgeActions = useMemo(
    () => actionsFetchNudgeNeeded(monitor, actionsCache),
    [monitor, actionsCache]
  );
  const fetchedAt = monitor?.fetched_at
    ? new Date(monitor.fetched_at).toLocaleString(lang === "gr" ? "el-GR" : "en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
  const actionsFetchedAt = actionsCache?.fetched_at
    ? new Date(actionsCache.fetched_at).toLocaleString(lang === "gr" ? "el-GR" : "en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  let status;
  if (loading && !available) {
    status = t("Fetching SoftOne Monitor…", "Λήψη SoftOne Monitor…");
  } else if (available && age) {
    status = t(`Work monitor cached since ${age}`, `Work monitor σε cache από ${age}`);
  } else if (available) {
    status = t("Work monitor loaded", "Το work monitor φορτώθηκε");
  } else {
    status = t("Work monitor not loaded yet", "Το work monitor δεν έχει φορτωθεί");
  }

  let actionsStatus = "";
  if (actionsAvailable && actionsAge) {
    actionsStatus = t(`Actions cached since ${actionsAge}`, `Ενέργειες σε cache από ${actionsAge}`);
  } else if (actionsAvailable) {
    actionsStatus = t("SoftOne actions loaded", "Οι SoftOne ενέργειες φορτώθηκαν");
  } else if (nudgeActions) {
    actionsStatus = t(
      "Monitor has filled slots — fetch SoftOne actions for WEB vs desktop audit",
      "Το monitor έχει slots — λάβετε SoftOne ενέργειες για έλεγχο WEB vs desktop"
    );
  } else {
    actionsStatus = t("SoftOne actions not loaded yet", "Οι SoftOne ενέργειες δεν έχουν φορτωθεί");
  }

  const busy = Boolean(refreshing || fetchingActions || loading || buttonsDisabled);
  const failedSend = notice?.tone === "danger" && notice?.message;
  return (
    <div
      style={{
        marginBottom: 10,
        padding: "10px 12px",
        borderRadius: 12,
        border: failedSend ? "1px solid #FECACA" : "1px solid #BFDBFE",
        background: failedSend ? "#FEF2F2" : "#EFF6FF",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      {notice?.message ? (
        <div
          role="status"
          style={{
            flexBasis: "100%",
            background: notice.tone === "danger" ? "#FEE2E2" : "#ECFDF5",
            border: `1px solid ${notice.tone === "danger" ? "#FCA5A5" : "#A7F3D0"}`,
            color: notice.tone === "danger" ? "#991B1B" : "#065F46",
            borderRadius: 10,
            padding: "8px 10px",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {notice.message}
        </div>
      ) : null}
      <div style={{ minWidth: 200 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#1E3A8A" }}>{status}</div>
        {available ? (
          <div style={{ fontSize: 12, color: "#1E40AF", marginTop: 3, fontWeight: 700 }}>
            {filled.length
              ? t(
                  `${filled.length} filled SoftOne slot${filled.length === 1 ? "" : "s"} this week`,
                  `${filled.length} συμπληρωμένα SoftOne slots αυτή την εβδομάδα`
                )
              : t("SoftOne has no filled slots this week", "Το SoftOne δεν έχει συμπληρωμένα slots αυτή την εβδομάδα")}
          </div>
        ) : null}
        <div style={{ fontSize: 11, color: "#1D4ED8", marginTop: 2, fontWeight: 600 }}>
          {fetchedAt
            ? t(`Last SoftOne Monitor fetch ${fetchedAt}`, `Τελευταία λήψη SoftOne Monitor ${fetchedAt}`)
            : t("Live SoftOne getWorkMonitor — not the warehouse ETL", "Live SoftOne getWorkMonitor — όχι το warehouse ETL")}
          {error ? ` · ${error}` : ""}
        </div>
        <div
          style={{
            fontSize: 11,
            color: nudgeActions ? "#9A3412" : "#1D4ED8",
            marginTop: 4,
            fontWeight: 600,
          }}
        >
          {actionsStatus}
          {actionsFetchedAt
            ? t(` · Last actions fetch ${actionsFetchedAt}`, ` · Τελευταία λήψη ενεργειών ${actionsFetchedAt}`)
            : ""}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {actions}
        <button
          type="button"
          className="btn-base btn-outline"
          disabled={busy}
          onClick={() => onRefresh?.()}
          style={{ minHeight: 36, whiteSpace: "nowrap" }}
        >
          {refreshing
            ? t("Fetching Monitor + actions…", "Λήψη Monitor + ενεργειών…")
            : t("Fetch SoftOne Monitor", "Λήψη SoftOne Monitor")}
        </button>
        <button
          type="button"
          className="btn-base btn-outline"
          disabled={busy || !onFetchActions}
          onClick={() => onFetchActions?.()}
          style={{
            minHeight: 36,
            whiteSpace: "nowrap",
            ...(nudgeActions
              ? { borderColor: "#FB923C", color: "#9A3412", background: "#FFF7ED" }
              : {}),
          }}
        >
          {fetchingActions || refreshing
            ? t("Fetching actions…", "Λήψη ενεργειών…")
            : t("Fetch SoftOne actions", "Λήψη SoftOne ενεργειών")}
        </button>
      </div>
    </div>
  );
}
